import type { TechlineCaseEntity } from '@packages/domain/techline/cases';

import { TextareaHelper, TextInputHelper, Typography } from '@packages/ui/shared';
import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { ConcernDetailsCheckboxes } from './concern-details-checkboxes';

const dataValidation = {
    maxMileage: 9999999,
};

const characterCountStyle = { color: '#4C525D', fontSize: 13, fontWeight: 400 } as const;
const labelStyle = { fontWeight: 400 } as const;

/**
 * Props for the ConcernDetailsStepContent component.
 */
export type ConcernDetailsStepContentProps = {
    /** The current case details data used to populate the form fields. */
    caseDetails: TechlineCaseEntity;

    /** Field-level errors computed by the validation hook. */
    errors: Record<string, string>;

    /** Callback to propagate partial case detail changes to the parent. */
    onCaseDetailsChange: (changes: Partial<TechlineCaseEntity>) => void;

    /** Callback to mark a field as touched (user has interacted with it). */
    onTouched: (field: string) => void;

    /** Map of field names to whether the user has interacted with them (controls error display). */
    touched: Record<string, boolean>;
};

/**
 * Form content for the Concern Details step of the Techline draft case stepper.
 * Renders repair order, date, mileage, customer concern fields and concern checkboxes.
 *
 * @param props - the component props
 * @returns the concern details step form content
 */
export function ConcernDetailsStepContent({
    caseDetails,
    errors,
    onCaseDetailsChange,
    onTouched,
    touched,
}: ConcernDetailsStepContentProps) {
    // State
    const [concernDetails, setConcernDetails] = useState({
        customerConcern: caseDetails?.customerConcern || '',
        mileage: caseDetails?.mileage ? Number(caseDetails.mileage).toLocaleString('en-US') : '',
        repairOrderNumber: caseDetails?.repairOrderNumber || '',
        repairOrderOpenDate: caseDetails?.repairOrderOpenDate ? caseDetails?.repairOrderOpenDate.split('T')[0] : '',
    });

    // Hooks
    const { t } = useTranslation();
    const classes = useStyles();

    // Forwarded callback aliases
    const handleCaseDetailsChange = onCaseDetailsChange;

    // Handlers

    /**
     * Handles blur on a concern detail field by trimming, marking touched,
     * and propagating the trimmed value to the parent.
     *
     * @param field - the concern detail field that lost focus
     */
    const handleBlur = useCallback(
        (field: keyof typeof concernDetails) => {
            const trimmedValue = concernDetails[field].trim();
            setConcernDetails((prev) => ({ ...prev, [field]: trimmedValue }));
            onTouched(field);
            handleCaseDetailsChange({ [field]: trimmedValue });
        },
        [concernDetails, handleCaseDetailsChange, onTouched]
    );

    /**
     * Handles text changes on the repair order number field by updating local state,
     * marking the field touched, and triggering validation.
     *
     * @param text - the new repair order number value
     */
    const handleRepairOrderNumberFieldChange = useCallback((text: string) => {
        setConcernDetails((prev) => ({ ...prev, repairOrderNumber: text }));
    }, []);

    /** Handles blur on the repair order number field. */
    const handleRepairOrderNumberFieldBlur = useCallback(() => {
        handleBlur('repairOrderNumber');
    }, [handleBlur]);

    /**
     * Handles text changes on the repair order open date field. Also syncs the value
     * immediately to the parent because date picker selections may not trigger blur before navigation.
     *
     * @param text - the new date value
     */
    const handleRepairOrderOpenDateFieldChange = useCallback(
        (text: string) => {
            setConcernDetails((prev) => ({ ...prev, repairOrderOpenDate: text }));
            onTouched('repairOrderOpenDate');
            // Sync immediately; date picker selections may not trigger blur before navigation
            handleCaseDetailsChange({ repairOrderOpenDate: text });
        },
        [handleCaseDetailsChange, onTouched]
    );

    /** Handles blur on the repair order open date field. */
    const handleRepairOrderOpenDateFieldBlur = useCallback(() => {
        handleBlur('repairOrderOpenDate');
    }, [handleBlur]);

    /**
     * Handles text changes on the mileage field by formatting the value and updating state.
     *
     * @param text - the raw mileage input
     */
    const handleMileageFieldChange = useCallback(
        (text: string) => {
            const formatted = formatMileage(text, concernDetails.mileage);
            setConcernDetails((prev) => ({ ...prev, mileage: formatted }));
        },
        [concernDetails.mileage]
    );

    /** Handles blur on the mileage field. */
    const handleMileageFieldBlur = useCallback(() => {
        const trimmed = concernDetails.mileage.trim();
        setConcernDetails((prev) => ({ ...prev, mileage: trimmed }));
        handleCaseDetailsChange({ mileage: Number(trimmed.replace(/\D/g, '')) || 0 });
        onTouched('mileage');
    }, [concernDetails.mileage, handleCaseDetailsChange, onTouched]);

    /**
     * Handles text changes on the customer concern textarea.
     *
     * @param text - the new customer concern value
     */
    const handleCustomerConcernFieldChange = useCallback((text: string) => {
        setConcernDetails((prev) => ({ ...prev, customerConcern: text }));
    }, []);

    /** Handles blur on the customer concern field. */
    const handleCustomerConcernFieldBlur = useCallback(() => {
        handleBlur('customerConcern');
    }, [handleBlur]);

    // Effects

    /**
     * Syncs local concern detail state when the parent case details change (e.g. after a save
     * round-trip or when navigating back to this step).
     */
    useEffect(() => {
        setConcernDetails({
            customerConcern: caseDetails?.customerConcern || '',
            mileage: caseDetails?.mileage ? Number(caseDetails.mileage).toLocaleString('en-US') : '',
            repairOrderNumber: caseDetails?.repairOrderNumber || '',
            repairOrderOpenDate: caseDetails?.repairOrderOpenDate ? caseDetails?.repairOrderOpenDate.split('T')[0] : '',
        });
    }, [caseDetails]);

    return (
        <div className={`${classes.container} flex-column`}>
            <div className="pb-3 pt-4">
                <Typography variant="h3">{t('techline:concernDetails.header')}</Typography>
            </div>
            <div className="d-flex flex-column flex-lg-row mt-3">
                <div className={`${classes.flexOne} mr-5`}>
                    <TextInputHelper
                        errorMessage={touched.repairOrderNumber ? t(errors.repairOrderNumber) : ''}
                        label={t('techline:concernDetails.repairOrder.label')}
                        labelStyle={labelStyle}
                        onBlur={handleRepairOrderNumberFieldBlur}
                        onChangeText={handleRepairOrderNumberFieldChange}
                        placeholder={t('techline:concernDetails.repairOrder.placeholder')}
                        required
                        type="text"
                        value={concernDetails.repairOrderNumber}
                    />
                </div>
                <div className={`${classes.flexOne} mr-5`}>
                    <TextInputHelper
                        errorMessage={touched.repairOrderOpenDate ? t(errors.repairOrderOpenDate) : ''}
                        label={t('techline:concernDetails.repairOpenDate.label')}
                        labelStyle={labelStyle}
                        maxDate={format(new Date(), 'yyyy-MM-dd')}
                        onBlur={handleRepairOrderOpenDateFieldBlur}
                        onChangeText={handleRepairOrderOpenDateFieldChange}
                        placeholder={t('techline:concernDetails.repairOpenDate.placeholder')}
                        required
                        type="date"
                        value={concernDetails.repairOrderOpenDate}
                    />
                </div>
                <div className={`${classes.flexOne} mr-5`}>
                    <TextInputHelper
                        errorMessage={touched.mileage ? t(errors.mileage) : ''}
                        keyboardType="numeric"
                        label={t('techline:concernDetails.mileage.label')}
                        labelStyle={labelStyle}
                        onBlur={handleMileageFieldBlur}
                        onChangeText={handleMileageFieldChange}
                        placeholder={t('techline:concernDetails.mileage.placeholder')}
                        required
                        value={concernDetails.mileage}
                    />
                </div>
            </div>
            <div className="mr-5 mt-5">
                <TextareaHelper
                    errorMessage={touched.customerConcern ? t(errors.customerConcern) : ''}
                    label={t('techline:concernDetails.customerConcern.label')}
                    labelRightElement={
                        <span style={characterCountStyle}>{`${
                            concernDetails.customerConcern.trim().length
                        }/2000`}</span>
                    }
                    labelStyle={labelStyle}
                    maxLength={2000}
                    onBlur={handleCustomerConcernFieldBlur}
                    onChangeText={handleCustomerConcernFieldChange}
                    placeholder={t('techline:concernDetails.customerConcern.placeholder')}
                    required
                    value={concernDetails.customerConcern}
                />
            </div>
            <ConcernDetailsCheckboxes
                caseDetails={caseDetails}
                errors={errors}
                onCaseDetailsChange={handleCaseDetailsChange}
                onTouched={onTouched}
                touched={touched}
            />
        </div>
    );
}

/**
 * Formats a raw mileage string into a locale-formatted number, stripping non-numeric
 * characters and capping at the maximum allowed mileage.
 *
 * @param value - the raw input string
 * @param currentMileage - the current mileage value to return if the input exceeds the max
 * @returns the formatted mileage string, or the current value if the input exceeds the max
 */
function formatMileage(value: string, currentMileage: string) {
    const numeric = value.replace(/[^0-9]/g, '');
    if (numeric === '') {
        return '';
    }
    const num = Number.parseInt(numeric, 10);
    if (Number.isNaN(num) || num > dataValidation.maxMileage) {
        return currentMileage;
    }
    return num.toLocaleString('en-US');
}

const useStyles = createUseStyles({
    container: {
        minHeight: 0,
        padding: '16px 80px',
    },
    flexOne: {
        flex: 1,
    },
});
